import { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, deleteItem } from './fitnessApi';
import { formatRestMmSs } from './workoutTiming';
import { totalVolumeLb, latestWeightLbs, liftBurnRange, formatBurnRange } from './liftingBurn';

const WORKOUTS_LIST = 'LiftingWorkouts';
const WEIGHINS_LIST = 'FitnessWeighIns';

function safeParse(json) {
  try { return JSON.parse(json) || []; } catch { return []; }
}

// History & Stats for lifting. TotalVolumeLb is the money metric — lifting's
// TotalDistanceMeters — so it leads every card and every stat block. The
// weigh-in view is the same FitnessWeighIns list the swim tab manages; it
// appears here too so a lifting day doesn't require a trip through the
// Swimming tab to log the morning weight.
function LiftingHistory({ getToken, userEmail }) {
  const [view, setView] = useState('history'); // history, stats, weighin
  const [workouts, setWorkouts] = useState([]);
  const [weighIns, setWeighIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newWeight, setNewWeight] = useState('');
  const [addingWeight, setAddingWeight] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const [wk, wi] = await Promise.all([
        getItems(token, WORKOUTS_LIST),
        getItems(token, WEIGHINS_LIST),
      ]);
      const myWorkouts = wk
        .filter(w => w.CreatedByEmail === userEmail)
        .map(w => ({
          id: w.id,
          title: w.Title || '',
          templateName: w.TemplateName || '',
          sets: safeParse(w.Sets),
          totalVolumeLb: w.TotalVolumeLb || 0,
          durationSeconds: w.DurationSeconds || 0,
          date: w.WorkoutDate || w.Created || '',
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setWorkouts(myWorkouts);

      const myWeighIns = wi
        .filter(w => w.CreatedByEmail === userEmail)
        .map(w => ({
          id: w.id,
          weight: w.WeightLbs || 0,
          WeightLbs: w.WeightLbs,
          WeighDate: w.WeighDate,
          date: w.WeighDate || w.Created || '',
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setWeighIns(myWeighIns);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, userEmail]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function handleDeleteWorkout(id) {
    if (!window.confirm('Delete this workout?')) return;
    try {
      const token = await getToken();
      await deleteItem(token, WORKOUTS_LIST, id);
      setWorkouts(prev => prev.filter(w => w.id !== id));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleAddWeighIn(e) {
    e.preventDefault();
    if (!newWeight) return;
    setAddingWeight(true);
    setError(null);
    try {
      const token = await getToken();
      const now = new Date().toISOString();
      await createItem(token, WEIGHINS_LIST, {
        Title: new Date().toLocaleDateString(),
        WeightLbs: parseFloat(newWeight),
        WeighDate: now,
        CreatedByEmail: userEmail,
      });
      setNewWeight('');
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingWeight(false);
    }
  }

  async function handleDeleteWeighIn(id) {
    try {
      const token = await getToken();
      await deleteItem(token, WEIGHINS_LIST, id);
      setWeighIns(prev => prev.filter(w => w.id !== id));
    } catch (err) {
      setError(err.message);
    }
  }

  function getStats(period) {
    const now = new Date();
    let start;
    if (period === 'week') {
      start = new Date(now);
      start.setDate(start.getDate() - 7);
    } else if (period === 'month') {
      start = new Date(now);
      start.setMonth(start.getMonth() - 1);
    } else {
      start = new Date(now);
      start.setFullYear(start.getFullYear() - 1);
    }
    const filtered = workouts.filter(w => new Date(w.date) >= start);
    const sessions = filtered.length;
    // The stored TotalVolumeLb is authoritative when present; recompute from
    // the sets only for rows that predate the column carrying a value.
    const volume = filtered.reduce((sum, w) => sum + (w.totalVolumeLb || totalVolumeLb(w.sets)), 0);
    return { sessions, volume };
  }

  if (loading) return <p className="Fitness-loading">Loading history...</p>;

  return (
    <div className="SwimHistory">
      {error && <p className="Fitness-error">{error}</p>}

      <div className="SwimHistory-view-tabs">
        {[
          { key: 'history', label: 'History' },
          { key: 'stats', label: 'Stats' },
          { key: 'weighin', label: 'Weigh-In' },
        ].map(t => (
          <button
            key={t.key}
            className={`SwimHistory-view-tab${view === t.key ? ' SwimHistory-view-tab--active' : ''}`}
            onClick={() => setView(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* HISTORY VIEW */}
      {view === 'history' && (
        <div className="SwimHistory-list">
          {workouts.length === 0 && <p className="Fitness-empty">No workouts recorded yet.</p>}
          {workouts.map(w => {
            const burn = liftBurnRange(latestWeightLbs(weighIns), w.durationSeconds);
            return (
              <div key={w.id} className="SwimHistory-card">
                <div className="SwimHistory-card-header" onClick={() => setExpandedId(expandedId === w.id ? null : w.id)}>
                  <div>
                    <strong>{w.templateName}</strong>
                    <span className="SwimHistory-card-date">{new Date(w.date).toLocaleDateString()}</span>
                  </div>
                  <div className="SwimHistory-card-meta">
                    <span>{Math.round(w.totalVolumeLb).toLocaleString('en-US')} lb</span>
                    {w.durationSeconds > 0 && <span>{Math.floor(w.durationSeconds / 60)}min</span>}
                  </div>
                </div>
                {expandedId === w.id && (
                  <div className="SwimHistory-card-detail">
                    {w.sets.map((s, i) => (
                      <div key={i} className="SwimHistory-set">
                        {s.sectionHeader && <em>{s.sectionHeader}: </em>}
                        {s.exercise} · {s.actualSets ?? s.sets}x{s.actualReps ?? s.reps}
                        {s.weightLb !== null && s.weightLb !== undefined ? ` @ ${s.weightLb} lb` : ''}
                        {Array.isArray(s.repRests) && s.repRests.some(r => Number(r) > 0)
                          ? ` (rests ${s.repRests.filter(r => Number(r) > 0).map(r => formatRestMmSs(r)).join(', ')})`
                          : ''}
                        {s.notes ? ` - ${s.notes}` : ''}
                        {s.partial ? ' (partial)' : ''}
                        {s.unplanned ? ' [added]' : ''}
                      </div>
                    ))}
                    {burn && <p className="LiftWorkout-burn">{formatBurnRange(burn)}</p>}
                    <button className="Fitness-btn Fitness-btn--small Fitness-btn--danger" onClick={() => handleDeleteWorkout(w.id)}>
                      Delete Workout
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* STATS VIEW — volume first, always */}
      {view === 'stats' && (
        <div className="SwimHistory-stats">
          {['week', 'month', 'year'].map(period => {
            const s = getStats(period);
            return (
              <div key={period} className="SwimHistory-stat-block">
                <h4>Last {period === 'week' ? '7 Days' : period === 'month' ? '30 Days' : '12 Months'}</h4>
                <div className="SwimHistory-stat-row">
                  <span>Total Volume</span>
                  <strong>{Math.round(s.volume).toLocaleString('en-US')} lb</strong>
                </div>
                <div className="SwimHistory-stat-row">
                  <span>Sessions</span>
                  <strong>{s.sessions}</strong>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* WEIGH-IN VIEW — the same FitnessWeighIns list the swim tab manages */}
      {view === 'weighin' && (
        <div className="SwimHistory-weighin">
          <form className="SwimHistory-weighin-form" onSubmit={handleAddWeighIn}>
            <input
              className="Fitness-input"
              type="number"
              step="0.1"
              min="0"
              placeholder="Weight (lbs)"
              value={newWeight}
              onChange={e => setNewWeight(e.target.value)}
              required
            />
            <button className="Fitness-btn" type="submit" disabled={addingWeight}>
              {addingWeight ? 'Saving...' : 'Log Weight'}
            </button>
          </form>

          {weighIns.length === 0 && <p className="Fitness-empty">No weigh-ins recorded yet.</p>}

          <div className="SwimHistory-weighin-list">
            {weighIns.map(w => (
              <div key={w.id} className="SwimHistory-weighin-entry">
                <span className="SwimHistory-weighin-date">{new Date(w.date).toLocaleDateString()}</span>
                <span className="SwimHistory-weighin-value">{w.weight} lbs</span>
                <button className="Fitness-btn-icon Fitness-btn-icon--delete" onClick={() => handleDeleteWeighIn(w.id)}>&times;</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default LiftingHistory;
